/**
 * HotelBillModal Component
 * Receipt-style modal displaying guest's hotel bill with grouped charges by category
 */

import { useEffect, useRef, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import type { HotelBill, BillCharge } from '@/types';
import { ChargeCategory } from '@/types';

const currencySymbol = (code: string) => code === 'EUR' ? '€' : code;
const fmt = (bill: HotelBill, amount: number) => `${currencySymbol(bill.currency)}${amount.toFixed(2)}`;

interface HotelBillModalProps {
  isOpen: boolean;
  onClose: () => void;
  bill?: HotelBill;
}

function getCategoryInfo(category: ChargeCategory): { name: string; icon: string; color: string } {
  const info: Record<ChargeCategory, { name: string; icon: string; color: string }> = {
    [ChargeCategory.ROOM]: { name: 'Room Charges', icon: '🏨', color: 'text-blue-400' },
    [ChargeCategory.MINIBAR]: { name: 'Minibar', icon: '🍫', color: 'text-orange-400' },
    [ChargeCategory.ROOM_SERVICE]: { name: 'Room Service', icon: '🍽️', color: 'text-amber-400' },
    [ChargeCategory.SPA]: { name: 'Spa & Wellness', icon: '💆', color: 'text-purple-400' },
    [ChargeCategory.RESTAURANT]: { name: 'Restaurant', icon: '�️', color: 'text-rose-400' },
    [ChargeCategory.BAR]: { name: 'Bar', icon: '🍸', color: 'text-pink-400' },
    [ChargeCategory.ACTIVITY]: { name: 'Activities', icon: '🏛️', color: 'text-amber-400' },
    [ChargeCategory.TRANSPORT]: { name: 'Transport', icon: '🚕', color: 'text-indigo-400' },
    [ChargeCategory.OTHER]: { name: 'Other Charges', icon: '📋', color: 'text-gray-400' },
  };
  return info[category] || info[ChargeCategory.OTHER];
}

export function HotelBillModal({ isOpen, onClose, bill }: HotelBillModalProps) {
  const scrollRef = useRef<HTMLDivElement>(null);

  const groupedCharges = useMemo(() => {
    if (!bill) return [];
    const groups: Record<ChargeCategory, BillCharge[]> = {
      [ChargeCategory.ROOM]: [], [ChargeCategory.MINIBAR]: [], [ChargeCategory.ROOM_SERVICE]: [],
      [ChargeCategory.SPA]: [], [ChargeCategory.RESTAURANT]: [], [ChargeCategory.BAR]: [],
      [ChargeCategory.ACTIVITY]: [], [ChargeCategory.TRANSPORT]: [], [ChargeCategory.OTHER]: [],
    };
    bill.charges.forEach(charge => { (groups[charge.category] || groups[ChargeCategory.OTHER]).push(charge); });
    return Object.entries(groups).filter(([, charges]) => charges.length > 0)
      .map(([category, charges]) => ({ category: category as ChargeCategory, charges, total: charges.reduce((sum, c) => sum + c.amount, 0) }));
  }, [bill]);

  useEffect(() => {
    if (!isOpen) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') { e.preventDefault(); onClose(); }
      else if (e.key === 'ArrowUp') { e.preventDefault(); scrollRef.current?.scrollBy({ top: -100, behavior: 'smooth' }); }
      else if (e.key === 'ArrowDown') { e.preventDefault(); scrollRef.current?.scrollBy({ top: 100, behavior: 'smooth' }); }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose]);

  if (!isOpen || !bill) return null;

  const formatDate = (dateStr: string) => new Date(dateStr).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  const formatDateTime = (dateStr: string) => new Date(dateStr).toLocaleDateString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit', hour12: false });

  return (
    <AnimatePresence>
      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
        className="fixed inset-0 z-[70] flex items-center justify-center bg-black/80 backdrop-blur-sm" onClick={onClose}>
        <motion.div initial={{ opacity: 0, scale: 0.95, y: 12 }} animate={{ opacity: 1, scale: 1, y: 0 }} exit={{ opacity: 0, scale: 0.95, y: 12 }}
          className="bg-zinc-950 rounded-2xl w-full max-w-xl max-h-[88vh] overflow-hidden shadow-2xl border border-white/10 flex flex-col"
          onClick={(e) => e.stopPropagation()}>

          {/* Receipt header */}
          <div className="px-6 pt-5 pb-4 border-b border-dashed border-white/15 flex-shrink-0">
            <div className="flex items-center justify-between mb-1">
              <h2 className="text-lg font-playfair tracking-wide text-white">HOTEL BILL</h2>
              <button onClick={onClose} className="w-8 h-8 rounded-full bg-white/10 flex items-center justify-center text-white/70 hover:bg-white/20 text-sm">✕</button>
            </div>
            <div className="flex items-center gap-2 text-white/50 text-xs">
              <span>{bill.guestName}</span>
              <span className="text-white/25">·</span>
              <span>Room {bill.roomNumber}</span>
              <span className="text-white/25">·</span>
              <span>{formatDate(bill.checkInDate)} – {formatDate(bill.checkOutDate)}</span>
            </div>
          </div>

          {/* Charge rows */}
          <div ref={scrollRef} className="flex-1 overflow-y-auto px-6 py-4 space-y-5">
            {groupedCharges.map(({ category, charges, total }, groupIdx) => {
              const categoryInfo = getCategoryInfo(category);
              return (
                <motion.div key={category} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: groupIdx * 0.06 }}>
                  {/* Section label */}
                  <div className={`flex items-center justify-between mb-2 pb-1 border-b border-white/8`}>
                    <span className={`text-xs uppercase tracking-widest font-semibold ${categoryInfo.color} flex items-center gap-1.5`}>
                      <span>{categoryInfo.icon}</span> {categoryInfo.name}
                    </span>
                    <span className={`text-xs font-mono ${categoryInfo.color}`}>{fmt(bill, total)}</span>
                  </div>
                  {/* Line items */}
                  <div className="space-y-2.5">
                    {charges.map((charge, chargeIdx) => (
                      <div key={charge.chargeId}>
                        <div className="flex items-start justify-between gap-3">
                          <div className="flex-1 min-w-0">
                            <p className="text-white/90 text-sm leading-snug truncate">{charge.description}</p>
                            <p className="text-white/35 text-xs mt-0.5">{formatDateTime(charge.datetime)}</p>
                          </div>
                          <span className="text-white/75 text-sm font-mono whitespace-nowrap">{fmt(bill, charge.amount)}</span>
                        </div>
                        {charge.items && charge.items.length > 0 && (
                          <div className="mt-1.5 ml-3 space-y-1">
                            {charge.items.map((item, itemIdx) => (
                              <div key={itemIdx} className="flex items-center justify-between text-xs text-white/40">
                                <span>{item.quantity}× {item.name}</span>
                                <span className="font-mono">{fmt(bill, item.quantity * item.unitPrice)}</span>
                              </div>
                            ))}
                          </div>
                        )}
                        {charge.notes && (!charge.items || charge.items.length === 0) && (
                          <div className="mt-1.5 ml-3 grid grid-cols-2 gap-x-4">
                            {charge.notes.split('\n').filter(Boolean).map((line, idx) => (
                              <div key={idx} className="flex items-center py-0.5">
                                <span className="w-1 h-1 rounded-full bg-amber-400/40 mr-1.5 flex-shrink-0" />
                                <span className="text-white/40 text-xs truncate">{line}</span>
                              </div>
                            ))}
                          </div>
                        )}
                        {chargeIdx < charges.length - 1 && <div className="border-b border-dotted border-white/8 mt-2.5" />}
                      </div>
                    ))}
                  </div>
                </motion.div>
              );
            })}
          </div>

          {/* Receipt footer total */}
          <div className="px-6 py-4 border-t border-dashed border-white/15 flex-shrink-0 bg-black/20">
            <div className="flex items-center justify-between">
              <span className="text-white/50 text-xs uppercase tracking-widest">Total Due</span>
              <span className="text-2xl font-playfair font-semibold text-teal-400">{fmt(bill, bill.totalAmount)}</span>
            </div>
            <p className="text-white/25 text-xs mt-1 text-right">Charges settled at checkout  ·  Esc to close</p>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}

export default HotelBillModal;
